import React, {useState} from 'react';
import styled from 'styled-components';
import {Typography} from '@/components/Typography';
import {MAP_LAYER_Z_INDEX} from '@/map/constants';
import {useOverlayScroll} from '@/map/hooks';
import {Marker, OverlayLoadFunc} from '@/map/modules';
import {interceptProps} from '@/styled-components';
import {palette} from '@/styles/palette';
import {withComma} from '@/utils';
import {CountBadge, DetailBox} from '../StyledComponents';
import {IRoundColorContentProps, RoundColorContent} from './RoundColorContent';

interface IContent {
  item: {
    name?: string | string[];
    count?: number;
    badgeCount?: number;
  };
  contentInfo: IRoundColorContentProps;
  detailBoxMaxHeight?: number;
  customDetailComponent?: JSX.Element;
}

export interface IRoundColorMarkerProps extends IContent {
  config: naver.maps.MarkerOptions & {
    onLoaded?: OverlayLoadFunc['onLoaded'];
  };
}

const DETAIL_BOX_SCROLL_CLASS = 'round-color-marker-scoll-box';

export const RoundColorMarker = React.memo(
  ({config, ...props}: IRoundColorMarkerProps) => {
    const onLoaded = (instance: naver.maps.OverlayView) =>
      config?.onLoaded?.(instance);

    return (
      <Marker
        draggable={false}
        clickable={false}
        {...config}
        onLoaded={onLoaded}
      >
        <Content {...props} />
      </Marker>
    );
  }
);

const Content = ({
  item,
  contentInfo,
  detailBoxMaxHeight,
  customDetailComponent,
}: IContent) => {
  const [hover, setHover] = useState<boolean>(false);
  const count = item.count && withComma(item.count);
  const name = item.name && [].concat(item.name as any);
  const hasCustomDetailComponent = !!customDetailComponent;
  const detailBoxClassNames = [
    hasCustomDetailComponent && 'custom-components-wrapper',
    !!detailBoxMaxHeight && detailBoxMaxHeight > 0 && DETAIL_BOX_SCROLL_CLASS,
  ]
    .filter((x) => !!x)
    .join(' ');

  const {color} = contentInfo;
  const hasScrollEvent = !!(
    detailBoxMaxHeight &&
    detailBoxMaxHeight > 0 &&
    item.badgeCount &&
    item.badgeCount > 0
  );
  useOverlayScroll(hasScrollEvent && hover);

  return (
    <Root
      className={hover ? 'active' : ''}
      onMouseOver={() => setHover(true)}
      onMouseOut={() => setHover(false)}
    >
      <RoundColorContent {...contentInfo} />
      {!!item.badgeCount && item.badgeCount > 0 && (
        <Badge>+{item.badgeCount}</Badge>
      )}
      {!!item.count && item.count > 0 && <Count color={color}>{count}</Count>}
      {(!!name?.length || hasCustomDetailComponent) && (
        <DetailWrapper className={hover ? 'show' : ''}>
          <Detail
            className={detailBoxClassNames}
            maxHeight={detailBoxMaxHeight}
          >
            {hasCustomDetailComponent
              ? customDetailComponent
              : !!name &&
                name.map((i, idx) => (
                  <Typography
                    key={idx}
                    title={i}
                    variant="medium12"
                    as="div"
                    ellipsis
                  >
                    {i}
                  </Typography>
                ))}
          </Detail>
        </DetailWrapper>
      )}
    </Root>
  );
};

const DetailWrapper = styled.div`
  position: absolute;
  top: 100%;
  padding-top: 2px;
  display: none;
  &.show {
    display: block;
  }
`;

const Detail = styled(DetailBox).withConfig({
  shouldForwardProp: interceptProps(['maxHeight']),
})<{
  maxHeight?: number;
}>`
  text-align: center;
  max-width: 120px;
  ${({maxHeight}) =>
    maxHeight &&
    `
    max-height: ${maxHeight}px;
    overflow: auto;
  `}
  &:not(.custom-components-wrapper) {
    ${Typography} + ${Typography} {
      margin-top: 2px;
    }
  }
`;

const Badge = styled(CountBadge)`
  position: absolute;
  right: -8.5px;
  top: -8px;
  z-index: 1;
`;

const Count = styled(Typography).attrs({
  variant: 'medium12',
  as: 'div',
})`
  font-weight: 700;
  text-shadow: -1px 0 ${palette.white}, 0 1px ${palette.white},
    1px 0 ${palette.white}, 0 -1px ${palette.white};
`;

const Root = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  flex-direction: column;
  left: -11px;
  top: -11px;
  z-index: ${MAP_LAYER_Z_INDEX.NEARBY_MARKER};
  &.active {
    z-index: ${MAP_LAYER_Z_INDEX.NEARBY_MARKER + 10};
  }
`;
